import type { CompetitionId, ConfigField, RulePack } from '../types';

export interface Competition {
  id: CompetitionId;
  name: string;
  arenaName: string;
  tagline: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  buildTime: string;
  image?: string;
  /** If set, shows a "Coming Soon" overlay on the card and disables entry. */
  comingSoon?: boolean;
  /* theme (literal Tailwind classes) */
  text: string;
  border: string;
  chip: string;
  button: string;
  glow: string;
  hoverBorder: string;
  hoverGlow: string;
  skills: string[];
  overview: string[];
  arenaFeatures: string[];
  rulesText: { section: string; rules: string[] }[];
  rulePack: RulePack;
  configFields: ConfigField[];
  presets: { name: string; values: Record<string, number | string> }[];
  faq: { q: string; a: string }[];
}

export const competitions: Competition[] = [
  /* ------------------------------------------------------------------ */
  {
    id: 'sumo',
    name: 'Sumo Robot',
    arenaName: 'Sumo Ring',
    tagline: 'Push your opponent out of the ring using smart sensing, speed, and strategy.',
    difficulty: 'Intermediate',
    buildTime: '4–8 hours',
    image: '/assets/competition arena.png',
    comingSoon: true,
    text: 'text-amber-300',
    border: 'border-amber-400/40',
    chip: 'bg-amber-400/10 text-amber-200 ring-amber-400/30',
    button: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:brightness-110',
    glow: 'shadow-[0_0_60px_-20px_rgba(251,191,36,0.5)]',
    hoverBorder: 'hover:border-amber-400/40',
    hoverGlow: 'hover:shadow-[0_0_60px_-20px_rgba(251,191,36,0.5)]',
    skills: ['Motors & torque', 'Edge detection', 'Opponent detection', 'Autonomous strategy'],
    overview: [
      'Two autonomous robots compete inside a circular ring. The goal is simple to say and hard to do: push the opponent out while staying inside the white border yourself.',
      'Winning sumo is a physics problem. Pushing power comes from traction (grip × weight), not just motor torque. A robot that finds the opponent first, attacks from the side, and reacts to the edge instantly wins most matches.',
      'In the simulator your robot fights a training opponent with real physics: mass, wheel friction, motor torque, collisions, and slipping are all computed by the Havok engine.',
    ],
    arenaFeatures: [
      'Circular black ring with a white border line',
      'Two starting zones facing each other',
      'Round timer with start countdown',
      'Autonomous training opponent',
      'Fall-off detection outside the ring',
    ],
    rulesText: [
      {
        section: 'Match rules',
        rules: [
          'Each match has 3 rounds; each round lasts 60–180 seconds.',
          'The robot starts after a countdown and must operate autonomously.',
          'The winner pushes the opponent outside the ring.',
          'A robot loses the round if any part of it touches outside the arena boundary.',
          'If both robots leave at the same time, the round is repeated.',
          'If no robot wins before time ends, points decide the round.',
        ],
      },
      {
        section: 'Robot restrictions',
        rules: [
          'Maximum size and weight are set in the rule pack (editable below).',
          'The robot must not damage the arena or intentionally damage the opponent.',
          'No dangerous weapons.',
          'No glue, suction, magnets, or sticky materials unless the rule pack allows them.',
          'The robot must fit inside the starting box before the round begins.',
        ],
      },
    ],
    rulePack: {
      arena: { shape: 'circle', diameterCm: 154, borderColor: 'white', surfaceColor: 'black', friction: 0.85 },
      robotLimits: { maxWidthCm: 20, maxLengthCm: 20, maxWeightKg: 3 },
      match: { rounds: 3, roundDurationSec: 90, startDelaySec: 3 },
      allowedParts: { motors: true, edgeSensors: true, distanceSensors: true, camera: false, magnets: false, stickyWheels: false },
      scoring: { pushOpponentOut: 3, opponentLeaves: 2, surviveToEnd: 1, selfOut: -2, noMovementPenalty: -1 },
    },
    configFields: [
      { key: 'weightKg', label: 'Robot weight', type: 'range', min: 0.5, max: 3, step: 0.1, unit: 'kg', default: 1.8, help: 'Heavier = harder to push, but slower to accelerate.' },
      { key: 'torqueNm', label: 'Motor torque (per motor)', type: 'range', min: 0.1, max: 1.2, step: 0.05, unit: 'N·m', default: 0.5, help: 'Pushing force = torque ÷ wheel radius, limited by traction.' },
      { key: 'gripMu', label: 'Wheel grip (friction μ)', type: 'range', min: 0.3, max: 1.2, step: 0.05, unit: 'μ', default: 0.8, help: 'Soft silicone ≈ 1.0+. Hard plastic ≈ 0.4.' },
      { key: 'maxSpeed', label: 'Top speed', type: 'range', min: 0.3, max: 1.5, step: 0.05, unit: 'm/s', default: 0.7 },
      { key: 'sensorRange', label: 'Opponent sensor range', type: 'range', min: 0.2, max: 1.5, step: 0.05, unit: 'm', default: 0.8 },
      { key: 'sensorCone', label: 'Sensor cone angle', type: 'range', min: 10, max: 90, step: 5, unit: '°', default: 40 },
      { key: 'reactionMs', label: 'Edge reaction delay', type: 'range', min: 20, max: 400, step: 10, unit: 'ms', default: 120, help: 'How fast your code reverses after the edge sensor fires.' },
      { key: 'strategy', label: 'Strategy', type: 'select', options: ['Hunter (attack on sight)', 'Spinner (search fast)', 'Defender (hold center)'], default: 'Hunter (attack on sight)' },
    ],
    presets: [
      { name: 'Balanced', values: { weightKg: 1.8, torqueNm: 0.5, gripMu: 0.8, maxSpeed: 0.7, sensorRange: 0.8, sensorCone: 40, reactionMs: 120, strategy: 'Hunter (attack on sight)' } },
      { name: 'Tank', values: { weightKg: 2.9, torqueNm: 0.9, gripMu: 1.05, maxSpeed: 0.45, sensorRange: 0.6, sensorCone: 50, reactionMs: 150, strategy: 'Defender (hold center)' } },
      { name: 'Speedy', values: { weightKg: 1.1, torqueNm: 0.35, gripMu: 0.7, maxSpeed: 1.2, sensorRange: 1.0, sensorCone: 30, reactionMs: 60, strategy: 'Spinner (search fast)' } },
    ],
    faq: [
      { q: 'Why does my robot leave the ring?', a: 'Two usual causes: edge reaction delay too high (your robot sees the white border but keeps driving), or top speed too high so it cannot stop in time. Lower the reaction delay below ~100 ms and reduce speed near the edge.' },
      { q: 'Why is my sumo robot weak at pushing?', a: 'Pushing force is limited by traction = grip × weight × 9.81. If your torque produces more force than traction, the wheels slip and you push with less than a lighter robot. Increase grip or weight, and keep the center of mass low.' },
      { q: 'Which sensor should I use to find the opponent?', a: 'Ultrasonic (HC-SR04) is cheap and works to ~1 m with a wide cone. ToF laser sensors are faster and more precise but narrower. Many winners use 2–3 sensors: front + angled sides.' },
      { q: 'How do I improve speed without losing control?', a: 'Raise speed but also lower the edge reaction delay; the faster you drive, the faster you must react. A good rule: braking distance (speed × reaction time) must be smaller than your edge margin.' },
    ],
  },

  /* ------------------------------------------------------------------ */
  {
    id: 'line',
    name: 'Line Follower',
    arenaName: 'Speed Track',
    tagline: 'Follow the line through curves and sharp turns — fastest valid time wins.',
    difficulty: 'Beginner',
    buildTime: '2–4 hours',
    text: 'text-cyan-300',
    border: 'border-cyan-400/40',
    chip: 'bg-cyan-400/10 text-cyan-200 ring-cyan-400/30',
    button: 'bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:brightness-110',
    glow: 'shadow-[0_0_60px_-20px_rgba(34,211,238,0.5)]',
    hoverBorder: 'hover:border-cyan-400/40',
    hoverGlow: 'hover:shadow-[0_0_60px_-20px_rgba(34,211,238,0.5)]',
    skills: ['IR sensor arrays', 'PID control', 'Speed tuning', 'Smooth driving'],
    overview: [
      'The robot follows a dark line on a light surface and must complete the track as fast as possible without losing the line.',
      'The secret is the PID controller: the sensor array measures how far the line is from your robot center (the error), and PID converts that error into steering. Too much Kp and the robot oscillates; too little and it misses curves.',
      'The simulated track has gentle curves and sharp turns. The sim computes sensor readings, turning radius, overshoot, and speed — exactly the trade-offs you will tune on a real robot.',
    ],
    arenaFeatures: [
      'Closed track with curves and sharp turns',
      'Start/finish timing line',
      'Live sensor-array readout',
      'Line-loss detection with penalties',
      'Speed graph after every run',
    ],
    rulesText: [
      {
        section: 'Match rules',
        rules: [
          'The robot starts behind the start line and follows the track autonomously.',
          'Time starts at the start line and ends at the finish line.',
          'If the robot loses the line, it receives a penalty.',
          'If the robot leaves the track completely, the run fails.',
          'Fastest valid time wins.',
        ],
      },
      {
        section: 'Track rules',
        rules: [
          'Track width, line color, and surface friction are configurable in the rule pack.',
          'Intersections and gaps can be added for advanced levels (phase 2).',
        ],
      },
    ],
    rulePack: {
      track: { lineWidthCm: 2.5, lineColor: 'black', surfaceColor: 'white', friction: 0.9 },
      match: { maxTimeSec: 120, lineLossPenaltySec: 3, maxLineLosses: 5 },
      scoring: { finishBase: 100, secondBonusUnder60: 1, lineLossPenalty: 5, smoothDrivingBonus: 10 },
    },
    configFields: [
      { key: 'baseSpeed', label: 'Base speed', type: 'range', min: 0.2, max: 1.4, step: 0.05, unit: 'm/s', default: 0.6 },
      { key: 'kp', label: 'Kp (proportional gain)', type: 'range', min: 0.5, max: 8, step: 0.1, default: 3, help: 'How strongly the robot steers back toward the line.' },
      { key: 'kd', label: 'Kd (derivative gain)', type: 'range', min: 0, max: 6, step: 0.1, default: 1.5, help: 'Damping — higher Kd reduces shaking/oscillation.' },
      { key: 'sensors', label: 'IR sensor count', type: 'select', options: ['3', '5', '8'], default: '5', help: 'More sensors = smoother error measurement.' },
      { key: 'curveSlowdown', label: 'Curve slowdown', type: 'range', min: 0, max: 0.8, step: 0.05, default: 0.4, help: 'How much the robot brakes when steering hard.' },
    ],
    presets: [
      { name: 'Stable learner', values: { baseSpeed: 0.45, kp: 2.5, kd: 1.2, sensors: '5', curveSlowdown: 0.5 } },
      { name: 'Race tune', values: { baseSpeed: 0.95, kp: 4.5, kd: 2.8, sensors: '8', curveSlowdown: 0.35 } },
      { name: 'Chaos (try it!)', values: { baseSpeed: 1.2, kp: 7, kd: 0.2, sensors: '3', curveSlowdown: 0.05 } },
    ],
    faq: [
      { q: 'My line follower keeps shaking. How do I fix the PID?', a: 'Shaking (oscillation) means Kp is too high relative to Kd. Lower Kp by ~25% or raise Kd until the robot settles. Tune at low speed first, then raise speed gradually.' },
      { q: 'Why does my robot fly off on sharp turns?', a: 'It is entering the turn too fast. Increase the curve slowdown so the robot brakes when steering is large, or lower base speed. Real champions brake before the curve, not inside it.' },
      { q: 'How many IR sensors do I need?', a: '3 works for gentle tracks. 5 is the sweet spot for most competitions. 8 gives a fine-grained error signal for high-speed racing.' },
      { q: 'How high should the sensors be from the ground?', a: '3–8 mm. Too high and ambient light washes out the contrast; too low and they scrape on bumps.' },
    ],
  },

  /* ------------------------------------------------------------------ */
  {
    id: 'maze',
    name: 'Maze Solver',
    arenaName: 'Labyrinth',
    tagline: 'Navigate from start to finish using sensors and a solving algorithm.',
    difficulty: 'Advanced',
    buildTime: '6–12 hours',
    comingSoon: true,
    text: 'text-emerald-300',
    border: 'border-emerald-400/40',
    chip: 'bg-emerald-400/10 text-emerald-200 ring-emerald-400/30',
    button: 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:brightness-110',
    glow: 'shadow-[0_0_60px_-20px_rgba(52,211,153,0.5)]',
    hoverBorder: 'hover:border-emerald-400/40',
    hoverGlow: 'hover:shadow-[0_0_60px_-20px_rgba(52,211,153,0.5)]',
    skills: ['Wall sensing', 'Algorithms', 'Turn calibration', 'Path planning'],
    overview: [
      'The robot must travel from the start cell to the finish cell of a maze without touching the walls.',
      'This is the most algorithmic competition. Wall-following (left-hand or right-hand rule) always escapes simple mazes but rarely takes the shortest path. Flood-fill / BFS finds the optimal route when the maze is known.',
      'The sim scores you on time, path efficiency versus the optimal path, and wall collisions — which depend on your turn calibration, exactly like a real robot with timed turns versus encoder/IMU turns.',
    ],
    arenaFeatures: [
      'Grid maze with walls, start and finish cells',
      'Front / left / right wall sensor readouts',
      'Choice of solving algorithm',
      'Path efficiency scoring vs the optimal route',
      'Collision detection on badly calibrated turns',
    ],
    rulesText: [
      {
        section: 'Match rules',
        rules: [
          'The robot starts in the start zone and must move autonomously.',
          'The robot must reach the finish zone.',
          'The robot should not touch or move walls — collisions are penalized.',
          'Best score is based on time, path efficiency, and penalties.',
        ],
      },
      {
        section: 'Maze modes',
        rules: [
          'Known Maze — the map is visible and you can plan the path (flood fill).',
          'Unknown Maze — the robot explores with wall-following rules.',
          'Memory Run (phase 2) — explore first, then re-run the fastest path.',
        ],
      },
    ],
    rulePack: {
      maze: { gridSize: 8, cellSizeCm: 30, wallHeightCm: 15 },
      match: { maxTimeSec: 180 },
      scoring: { finishBase: 100, timeBonusMax: 50, efficiencyBonusMax: 50, wallCollisionPenalty: 10, stuckPenalty: 20 },
    },
    configFields: [
      { key: 'algorithm', label: 'Solving algorithm', type: 'select', options: ['Left-hand rule', 'Right-hand rule', 'Flood fill (known maze)'], default: 'Left-hand rule' },
      { key: 'speed', label: 'Drive speed', type: 'range', min: 0.2, max: 1.0, step: 0.05, unit: 'm/s', default: 0.5 },
      { key: 'turnCalibration', label: 'Turn calibration', type: 'range', min: 0.5, max: 1, step: 0.05, default: 0.8, help: '1.0 = perfect encoder/IMU turns. Low values risk wall hits.' },
      { key: 'sensorNoise', label: 'Sensor quality', type: 'select', options: ['Ultrasonic (noisy)', 'ToF laser (clean)'], default: 'Ultrasonic (noisy)' },
    ],
    presets: [
      { name: 'Explorer', values: { algorithm: 'Left-hand rule', speed: 0.45, turnCalibration: 0.8, sensorNoise: 'Ultrasonic (noisy)' } },
      { name: 'Optimal racer', values: { algorithm: 'Flood fill (known maze)', speed: 0.85, turnCalibration: 0.95, sensorNoise: 'ToF laser (clean)' } },
    ],
    faq: [
      { q: 'Which algorithm should I pick?', a: 'Left/right-hand rule is simple and always escapes simply-connected mazes, but the path is long. Flood fill (BFS) takes the shortest path but needs the maze map — use it in Known Maze mode. Competitive micromouse robots explore first, then flood-fill.' },
      { q: 'My robot keeps hitting walls on turns. Why?', a: 'Your turn angle is not calibrated — timed turns drift with battery voltage. Raise turn calibration (in real life: add wheel encoders or an IMU for exact 90° turns).' },
      { q: 'What sensors do I need for walls?', a: 'Three distance sensors: front, left, right. ToF lasers (VL53L0X) are cleaner than ultrasonic in tight corridors where echoes bounce.' },
      { q: 'My path works but is not optimal. How do I improve?', a: 'Wall-following found *a* path, not the best one. Switch to flood fill in Known Maze mode and compare the path-efficiency score.' },
    ],
  },

  /* ------------------------------------------------------------------ */
  {
    id: 'battle',
    name: 'Robot Battle',
    arenaName: 'Battle Arena',
    tagline: 'Strategy-based, non-destructive combat: push, block, control zones, survive.',
    difficulty: 'Intermediate',
    buildTime: '5–10 hours',
    comingSoon: true,
    text: 'text-violet-300',
    border: 'border-violet-400/40',
    chip: 'bg-violet-400/10 text-violet-200 ring-violet-400/30',
    button: 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:brightness-110',
    glow: 'shadow-[0_0_60px_-20px_rgba(167,139,250,0.5)]',
    hoverBorder: 'hover:border-violet-400/40',
    hoverGlow: 'hover:shadow-[0_0_60px_-20px_rgba(167,139,250,0.5)]',
    skills: ['Momentum & collisions', 'Zone control', 'Energy management', 'Strategy design'],
    overview: [
      'An educational, non-destructive robot battle. No weapons — the focus is strategy: pushing, blocking, avoiding, and controlling the center zone.',
      'In the simulation, damage is just a game mechanic: hard hits drain the opponent\'s virtual energy. Real-world versions of this competition use the same robots as sumo, in a walled arena with scoring zones.',
      'Physics matters: momentum (mass × speed) decides who wins a head-on collision, and a low center of mass keeps your robot from tipping when hit.',
    ],
    arenaFeatures: [
      'Walled rectangular arena — nobody falls out',
      'Glowing center control zone (+points over time)',
      'Virtual energy bars (simulation only)',
      'Hit detection based on collision momentum',
      'Match timer with survival bonus',
    ],
    rulesText: [
      {
        section: 'Match rules',
        rules: [
          'Two robots enter the arena; the match lasts 2–3 minutes.',
          'Points are awarded for pushing, hard hits, controlling the center zone, and surviving.',
          'No dangerous weapons in real-world mode.',
          'In virtual mode, damage is simulated safely as energy points.',
          'A robot whose energy reaches zero loses immediately.',
        ],
      },
      {
        section: 'Modes',
        rules: [
          'Autonomous Battle — the robot decides alone (this sim).',
          'Remote-Control Battle — user drives (phase 2).',
          'Strategy Battle — write behavior logic before the match (phase 2).',
        ],
      },
    ],
    rulePack: {
      arena: { widthCm: 200, lengthCm: 200, wallHeightCm: 10, friction: 0.8 },
      robotLimits: { maxWeightKg: 3, maxWidthCm: 25 },
      match: { durationSec: 120, startDelaySec: 3, startEnergy: 100 },
      scoring: { hardHit: 3, zoneControlPer5Sec: 2, surviveToEnd: 1, energyKO: 10 },
    },
    configFields: [
      { key: 'weightKg', label: 'Robot weight', type: 'range', min: 0.5, max: 3, step: 0.1, unit: 'kg', default: 2.0 },
      { key: 'torqueNm', label: 'Motor torque (per motor)', type: 'range', min: 0.1, max: 1.2, step: 0.05, unit: 'N·m', default: 0.55 },
      { key: 'gripMu', label: 'Wheel grip (friction μ)', type: 'range', min: 0.3, max: 1.2, step: 0.05, unit: 'μ', default: 0.85 },
      { key: 'maxSpeed', label: 'Top speed', type: 'range', min: 0.3, max: 1.6, step: 0.05, unit: 'm/s', default: 0.8 },
      { key: 'sensorRange', label: 'Sensor range', type: 'range', min: 0.3, max: 2, step: 0.05, unit: 'm', default: 1.0 },
      { key: 'strategy', label: 'Strategy', type: 'select', options: ['Aggressive', 'Defensive (hold zone)', 'Balanced'], default: 'Balanced' },
    ],
    presets: [
      { name: 'Bruiser', values: { weightKg: 2.8, torqueNm: 1.0, gripMu: 1.0, maxSpeed: 0.6, sensorRange: 1.0, strategy: 'Aggressive' } },
      { name: 'Zone keeper', values: { weightKg: 2.2, torqueNm: 0.6, gripMu: 0.95, maxSpeed: 0.7, sensorRange: 1.4, strategy: 'Defensive (hold zone)' } },
      { name: 'Skirmisher', values: { weightKg: 1.2, torqueNm: 0.4, gripMu: 0.75, maxSpeed: 1.4, sensorRange: 1.6, strategy: 'Balanced' } },
    ],
    faq: [
      { q: 'Aggressive, defensive, or balanced — which strategy?', a: 'Aggressive wins against slow robots but burns energy on missed charges. Defensive (holding the center zone) accumulates steady points and forces the opponent to come to you. Balanced adapts. Against an unknown opponent, start balanced.' },
      { q: 'My robot is powerful but too slow. What do I change?', a: 'Drop a little weight or raise top speed; momentum = mass × velocity, so a medium-weight fast robot can out-hit a heavy slow one.' },
      { q: 'My robot tips over when hit.', a: 'Your center of mass is too high. Mount the battery low, widen the chassis, and reduce top speed slightly — tipping usually happens during your own turns.' },
      { q: 'My defensive strategy loses. Why?', a: 'Check sensor range — a defender that cannot see the attacker early gets hit from the side. Range should exceed the arena half-width.' },
    ],
  },
];

export function getCompetition(id: string): Competition | undefined {
  return competitions.find((c) => c.id === id);
}

/** The user journey shown on the dashboard. */
export const journey = [
  'Choose competition',
  'Read rules',
  'Build robot',
  'Choose parts',
  'Write code',
  'Simulate',
  'Debug with Taa Bot',
  'Improve strategy',
  'Submit score',
];
